/**
 * `NotificationSettings` — GVW-153 notifications preferences.
 *
 * Per brief §2 / §5 / §7: per-category opt-in for the inbox (GVW-012/135).
 * Critical alerts always show — the critical toggle is `aria-disabled="true"`
 * with helper "Critical alerts always appear and cannot be disabled" per
 * brief §7 / §8.
 *
 * Persistence: localStorage fallback under `gvw-set-notifications`.
 *
 * TODO(WP-E11): when `@geosoft/platform-sdk` exposes a real notification-prefs
 * endpoint, swap `localStorage` for `sdk.notifications.setPrefs(...)`.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-153/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

type Category = 'critical' | 'system' | 'layers' | 'sharing' | 'jobs' | 'updates';

interface NotificationPrefs {
  categories: Record<Category, boolean>;
}

const STORAGE_KEY = 'gvw-set-notifications';

const CATEGORIES: ReadonlyArray<{ id: Category; label: string; description: string; icon: string; alwaysOn: boolean }> = [
  { id: 'critical', label: 'Critical alerts', description: 'System errors, security issues, urgent warnings.', icon: 'warning', alwaysOn: true },
  { id: 'system', label: 'System notifications', description: 'Maintenance windows, service updates.', icon: 'settings', alwaysOn: false },
  { id: 'layers', label: 'Layer notifications', description: 'Sync status, credential expiration, layer health.', icon: 'layers', alwaysOn: false },
  { id: 'sharing', label: 'Sharing activity', description: 'Workspace shares, access requests.', icon: 'share', alwaysOn: false },
  { id: 'jobs', label: 'Background jobs', description: 'Upload progress, processing completion.', icon: 'work', alwaysOn: false },
  { id: 'updates', label: 'Product updates', description: "What's new and feature announcements.", icon: 'new_releases', alwaysOn: false },
];

const DEFAULTS: NotificationPrefs = {
  categories: {
    critical: true,
    system: true,
    layers: true,
    sharing: true,
    jobs: true,
    updates: false,
  },
};

export function NotificationSettings() {
  const liveRegion = useLiveRegion();
  const [settings, setSettings] = useState<NotificationPrefs>(DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as NotificationPrefs;
        // Critical is always on regardless of stored value.
        parsed.categories.critical = true;
        setSettings(parsed);
      }
    } catch {
      /* defaults */
    }
  }, []);

  useEffect(() => {
    document.title = 'Notifications · GeoViewer';
    liveRegion.announce('status', 'Notification preferences loaded.');
  }, [liveRegion]);

  const handleToggle = useCallback((id: Category) => {
    if (id === 'critical') return;
    setSettings((prev) => ({
      ...prev,
      categories: { ...prev.categories, [id]: !prev.categories[id] },
    }));
  }, []);

  const handleSave = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      const enabled = Object.values(settings.categories).filter(Boolean).length;
      liveRegion.announce('status', `Notification preferences saved. ${enabled} of ${CATEGORIES.length} categories enabled.`);
    } catch {
      liveRegion.announce('alert', 'Could not save notification preferences.');
    }
  }, [settings, liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-153-heading"
      data-gvw-id="GVW-153"
      data-route="/workspace/settings/notifications"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-153-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          notifications
        </span>
        Notification preferences
      </h1>

      <p style={{ marginTop: 0, marginBottom: tokens.space[5], color: tokens.color.ink.secondary, fontSize: tokens.font.size.md }}>
        Choose which categories of notifications appear in your inbox. Critical alerts always appear.
      </p>

      {saved ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.success}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Notification preferences saved.
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3], maxWidth: 640 }}
      >
        {CATEGORIES.map((cat) => {
          const enabled = settings.categories[cat.id];
          const helperId = `gvw-153-${cat.id}-help`;
          return (
            <div
              key={cat.id}
              style={{
                padding: tokens.space[3],
                background: tokens.color.surface[1],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
              }}
            >
              <label style={{ display: 'flex', gap: tokens.space[3], alignItems: 'flex-start', cursor: cat.alwaysOn ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={enabled}
                  aria-disabled={cat.alwaysOn}
                  aria-describedby={helperId}
                  disabled={cat.alwaysOn}
                  checked={enabled}
                  onChange={() => handleToggle(cat.id)}
                  style={{ marginTop: tokens.space[1] }}
                />
                <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary, marginTop: tokens.space[1] }}>
                  {cat.icon}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
                    {cat.label}
                    {cat.alwaysOn ? (
                      <span
                        style={{
                          fontSize: tokens.font.size.xs,
                          padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                          background: tokens.color.surface[3],
                          color: tokens.color.ink.secondary,
                          borderRadius: tokens.radius.chip,
                          fontWeight: tokens.font.weight.regular,
                        }}
                      >
                        Always on
                      </span>
                    ) : null}
                  </div>
                  <div id={helperId} style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary, marginTop: tokens.space[1] }}>
                    {cat.description}
                    {cat.alwaysOn ? ' Critical alerts always appear and cannot be disabled.' : ''}
                  </div>
                </div>
              </label>
            </div>
          );
        })}

        <div style={{ marginTop: tokens.space[3] }}>
          <button
            type="submit"
            style={{
              padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              cursor: 'pointer',
            }}
          >
            Save preferences
          </button>
        </div>
      </form>
    </main>
  );
}
